import React from "react";
import { useColorMode } from "@chakra-ui/react";
import {
  FiArrowRight,
  FiBarChart2,
  FiBriefcase,
  FiCheckCircle,
  FiClock,
  FiCode,
  FiHeadphones,
  FiLayers,
  FiShield,
  FiTarget,
  FiUsers,
  FiZap,
} from "react-icons/fi";
import {
  FaCss3Alt,
  FaGitAlt,
  FaHtml5,
  FaNodeJs,
  FaPython,
  FaReact,
} from "react-icons/fa";
import {
  SiFlask,
  SiGreensock,
  SiJavascript,
  SiMongodb,
  SiPostgresql,
  SiTailwindcss,
} from "react-icons/si";
import HireToBuildVisual from "../../assets/images/HireToBuild.gif";
import InstaDev from "../../assets/images/InstaDevLogo.png";
import LetsCrft from "../../assets/images/LetscrftLogo.png";
import NRICS from "../../assets/images/NRICSLogo.png";
import PricingCard from "./PricingCard";
import SectionWrapper from "./SectionWrapper";
import ServiceCard from "./ServiceCard";
import "./HireToBuild.css";

const services = [
  {
    icon: FiUsers,
    title: "Vetted Product Talent",
    description:
      "Access interns and junior engineers screened for technical depth, communication, reliability, and startup readiness.",
  },
  {
    icon: FiCode,
    title: "Dedicated Build Pods",
    description:
      "Assemble frontend, backend, UI, QA, and automation talent around one delivery rhythm instead of isolated hiring.",
  },
  {
    icon: FiTarget,
    title: "Role-Based Matching",
    description:
      "We map your roadmap, tech stack, and team culture to candidates who can contribute from the first sprint.",
  },
  {
    icon: FiBarChart2,
    title: "Performance Governance",
    description:
      "Get structured onboarding, progress reviews, replacement support, and transparent coordination through the engagement.",
  },
  {
    icon: FiLayers,
    title: "Flexible Scale-Up",
    description:
      "Start with one specialist, then expand into a managed pod as milestones, releases, or support needs increase.",
  },
  {
    icon: FiShield,
    title: "Enterprise-Ready Support",
    description:
      "Clear expectations, documented workflows, and responsive account support for teams that need predictable delivery.",
  },
];

const technologies = [
  { name: "HTML5", icon: FaHtml5, color: "#E34F26" },
  { name: "CSS3", icon: FaCss3Alt, color: "#1572B6" },
  { name: "JavaScript", icon: SiJavascript, color: "#D6B900" },
  { name: "React", icon: FaReact, color: "#028EC4" },
  { name: "Node.js", icon: FaNodeJs, color: "#3C873A" },
  { name: "Tailwind CSS", icon: SiTailwindcss, color: "#028EC4" },
  { name: "GSAP", icon: SiGreensock, color: "#61A60E" },
  { name: "Python", icon: FaPython, color: "#3776AB" },
  { name: "Flask", icon: SiFlask, color: "#111827" },
  { name: "MongoDB", icon: SiMongodb, color: "#47A248" },
  { name: "PostgreSQL", icon: SiPostgresql, color: "#336791" },
  { name: "Git", icon: FaGitAlt, color: "#F05032" },
];

const processSteps = [
  {
    title: "Requirement Analysis",
    description:
      "We clarify product goals, team gaps, delivery expectations, stack requirements, and success metrics.",
  },
  {
    title: "Team Allocation",
    description:
      "Shortlisted talent is matched to your workflow with onboarding notes, communication cadence, and role clarity.",
  },
  {
    title: "Development Sprint",
    description:
      "Your team gets sprint-ready contributors with GrapplTech coordination, review checkpoints, and delivery follow-up.",
  },
  {
    title: "Delivery & Scaling",
    description:
      "Scale from a single contributor to a larger pod as releases, maintenance, and customer needs grow.",
  },
];

const pricingPlans = [
  {
    name: "Starter",
    price: "18% - 22%",
    description: "Best for MVP teams hiring one or two contributors for focused product execution.",
    features: [
      "Human-vetted candidate shortlist",
      "Role-specific technical screening",
      "Guided onboarding support",
      "Monthly performance check-ins",
      "30-day replacement assistance",
    ],
  },
  {
    name: "Growth",
    price: "14% - 18%",
    description: "Built for scaling teams that need a dependable pod across multiple active workstreams.",
    recommended: true,
    features: [
      "Priority sourcing and matching",
      "Dedicated account coordinator",
      "Bi-weekly performance reporting",
      "15-day replacement support",
      "Conversion-to-hire assistance",
    ],
  },
  {
    name: "Enterprise",
    price: "Custom",
    description: "For long-term engagements, multiple teams, or structured hiring programs with governance needs.",
    features: [
      "Custom talent pipeline design",
      "Weekly delivery and talent syncs",
      "Priority replacement workflow",
      "Performance analytics reviews",
      "Quarterly business alignment",
    ],
  },
];

const reasons = [
  {
    icon: FiUsers,
    title: "Experienced engineers",
    description: "Candidates are assessed on practical build skills, collaboration habits, and ownership mindset.",
  },
  {
    icon: FiBriefcase,
    title: "Startup and enterprise exposure",
    description: "Our teams understand lean delivery cycles and the documentation standards larger teams expect.",
  },
  {
    icon: FiClock,
    title: "Fast delivery cycles",
    description: "Move from requirement intake to productive contributors in days, not months.",
  },
  {
    icon: FiHeadphones,
    title: "Dedicated support",
    description: "A coordination layer keeps hiring, onboarding, replacements, and feedback loops running smoothly.",
  },
];

const testimonials = [
  {
    name: "Akhil Varma",
    role: "Founder",
    company: "LetsCrft Online",
    logo: LetsCrft,
    feedback:
      "GrapplTech helped us expand execution capacity without slowing down leadership. The candidates were prepared, communicative, and able to contribute to live product work quickly.",
  },
  {
    name: "Meghana Rao",
    role: "Program Lead",
    company: "NRICodeSchool",
    logo: NRICS,
    feedback:
      "The matching quality stood out. We got people who understood our education products, worked well with mentors, and kept momentum across multiple releases.",
  },
  {
    name: "Rohit Kumar",
    role: "Delivery Manager",
    company: "InstaDev",
    logo: InstaDev,
    feedback:
      "Their coordination layer saved our team hours every week. We could scale project support while keeping delivery ownership and communication clean.",
  },
];

const stats = [
  { value: "400+", label: "Talent deployments" },
  { value: "7-14", label: "Days to shortlist" },
  { value: "10+", label: "Startup partners" },
  { value: "4.8/5", label: "Client satisfaction" },
];

const buttonBase =
  "inline-flex h-12 items-center justify-center rounded-xl px-5 text-sm font-semibold no-underline transition-all duration-200 active:scale-[0.98]";

const HireToBuild = () => {
  const { colorMode } = useColorMode();
  const pageClassName = `htb-page ${
    colorMode === "dark" ? "htb-page--dark" : "htb-page--light"
  } bg-slate-50 font-body text-slate-700 [&_a]:no-underline [&_p]:m-0`;

  return (
    <main className={pageClassName}>
      <section className="htb-hero relative overflow-hidden border-b border-slate-200 bg-[radial-gradient(circle_at_top_left,rgba(2,142,196,0.14),transparent_34%),linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)]">
        <div className="htb-hero-grid mx-auto grid min-h-[720px] max-w-7xl items-center gap-12 px-5 py-16 sm:px-6 lg:grid-cols-[1.05fr_0.95fr] lg:px-8 lg:py-20">
          <div className="htb-hero-copy">
            <div className="htb-pill mb-6 inline-flex items-center rounded-full border border-brand-200 bg-white px-4 py-2 text-sm font-semibold text-brand-700 shadow-soft">
              Hire-to-Build for product teams
            </div>
            <h1 className="htb-hero-title max-w-4xl font-display text-5xl font-bold leading-[1.05] tracking-tight text-slate-950 sm:text-[48px] lg:text-[64px]">
              Build product teams that ship from sprint one.
            </h1>
            <p className="htb-hero-description mt-6 max-w-2xl text-lg leading-8 text-slate-600">
              GrapplTech helps startups and growing companies hire vetted technology
              talent, form managed build pods, and scale delivery without the drag
              of traditional recruitment.
            </p>
            <div className="htb-button-row mt-8 flex flex-col gap-3 sm:flex-row">
              <a
                href="/contact"
                className={`htb-button htb-button-primary ${buttonBase} bg-brand-600 text-white shadow-brand hover:-translate-y-0.5 hover:bg-brand-700 hover:shadow-lift`}
              >
                Request a Demo
                <FiArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
              </a>
              <a
                href="/contact"
                className={`htb-button htb-button-secondary ${buttonBase} border border-slate-300 bg-white text-slate-900 hover:-translate-y-0.5 hover:border-brand-400 hover:text-brand-700 hover:shadow-soft`}
              >
                Talk to an Expert
              </a>
            </div>

            <div className="htb-stats-grid mt-10 grid max-w-2xl grid-cols-2 gap-3 sm:grid-cols-4">
              {stats.map((stat) => (
                <div key={stat.label} className="htb-stat-card rounded-2xl border border-slate-200 bg-white p-4 shadow-soft">
                  <p className="htb-stat-value font-display text-2xl font-bold tracking-tight text-slate-950">
                    {stat.value}
                  </p>
                  <p className="htb-stat-label mt-1 text-sm leading-5 text-slate-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="htb-visual-wrap relative">
            <div className="htb-visual-card rounded-[28px] border border-slate-200 bg-white p-4 shadow-lift">
              <div className="htb-visual-header">
                <div>
                  <p className="htb-visual-kicker">Talent pipeline</p>
                  <p className="htb-visual-title">Build pod readiness</p>
                </div>
                <span className="htb-visual-status">Live</span>
              </div>
              <div className="htb-visual-stage">
                <div className="htb-visual-orbit htb-visual-orbit-one" />
                <div className="htb-visual-orbit htb-visual-orbit-two" />
                <img
                  src={HireToBuildVisual}
                  alt="Hire to Build talent and product delivery illustration"
                  className="htb-visual h-auto w-full rounded-2xl object-cover"
                />
                <div className="htb-floating-card htb-floating-card-top">
                  <span>12</span>
                  <p>Vetted profiles</p>
                </div>
                <div className="htb-floating-card htb-floating-card-bottom">
                  <span>92%</span>
                  <p>Sprint fit score</p>
                </div>
              </div>
              <div className="htb-proof-grid grid gap-3 p-4 sm:grid-cols-3">
                {["Screened talent", "Managed onboarding", "Sprint tracking"].map((item) => (
                  <div key={item} className="htb-proof-item flex items-center gap-2 rounded-xl bg-slate-50 px-3 py-3 text-sm font-semibold text-slate-700">
                    <FiCheckCircle className="h-4 w-4 text-brand-600" aria-hidden="true" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <SectionWrapper
        eyebrow="What We Offer"
        title="A flexible hiring engine for modern product delivery"
        description="Every engagement is designed to reduce hiring friction, increase delivery confidence, and give your internal team more room to focus."
      >
        <div className="htb-services-grid grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {services.map((service) => (
            <ServiceCard key={service.title} {...service} />
          ))}
        </div>
      </SectionWrapper>

      <SectionWrapper
        eyebrow="Technology"
        title="Technologies We Excel In"
        description="We keep the talent pool close to modern product stacks used by fast-moving web, SaaS, automation, and data teams."
        className="bg-white"
      >
        <div className="htb-tech-grid grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {technologies.map((tech) => {
            const Icon = tech.icon;
            return (
              <article
                key={tech.name}
                className="htb-card htb-tech-card group rounded-2xl border border-slate-200 bg-white p-5 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
              >
                <Icon
                  className="htb-tech-icon mx-auto h-10 w-10 transition-transform duration-300 group-hover:scale-110"
                  style={{ color: tech.color }}
                  aria-hidden="true"
                />
                <p className="htb-tech-name mt-4 text-sm font-semibold text-slate-800">{tech.name}</p>
              </article>
            );
          })}
        </div>
      </SectionWrapper>

      <SectionWrapper
        eyebrow="Process"
        title="From requirement to delivery in four clear steps"
        description="The process is structured enough for enterprise teams and light enough for startups that need speed."
      >
        <div className="htb-process-grid grid gap-5 lg:grid-cols-4">
          {processSteps.map((step, index) => (
            <article
              key={step.title}
              className="htb-card htb-process-card relative rounded-2xl border border-slate-200 bg-white p-6 shadow-soft transition-all duration-300 hover:-translate-y-1 hover:border-brand-200 hover:shadow-lift"
            >
              <span className="htb-step-number font-display text-sm font-bold text-brand-600">
                0{index + 1}
              </span>
              <h3 className="htb-card-title mt-5 font-display text-2xl font-medium leading-tight text-slate-900">
                {step.title}
              </h3>
              <p className="htb-card-text mt-3 text-sm leading-6 text-slate-600">{step.description}</p>
            </article>
          ))}
        </div>
      </SectionWrapper>

      <SectionWrapper
        eyebrow="Pricing"
        title="Enterprise-grade pricing that stays transparent"
        description="Startups pay talent directly. GrapplTech charges a clear service commission for sourcing, screening, onboarding, coordination, and performance tracking."
        className="bg-white"
      >
        <div className="htb-pricing-grid grid items-stretch gap-6 lg:grid-cols-3">
          {pricingPlans.map((plan) => (
            <PricingCard key={plan.name} {...plan} />
          ))}
        </div>
        <p className="htb-pricing-note mx-auto mt-8 max-w-3xl rounded-2xl border border-brand-100 bg-brand-50 px-5 py-4 text-center text-sm leading-6 text-brand-800">
          Example: for a monthly stipend of INR 15,000, the GrapplTech service
          fee is calculated against the selected engagement tier.
        </p>
      </SectionWrapper>

      <SectionWrapper
        eyebrow="Why Choose Us"
        title="Built for teams that care about quality, speed, and accountability"
      >
        <div className="htb-reasons-grid grid gap-5 md:grid-cols-2">
          {reasons.map((reason) => {
            const Icon = reason.icon;
            return (
              <article key={reason.title} className="htb-card htb-reason-card flex gap-5 rounded-2xl border border-slate-200 bg-white p-6 shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift">
                <div className="htb-icon-box flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-brand-50 text-brand-600">
                  <Icon className="h-6 w-6" aria-hidden="true" />
                </div>
                <div>
                  <h3 className="htb-card-title font-display text-2xl font-medium leading-tight text-slate-900">
                    {reason.title}
                  </h3>
                  <p className="htb-card-text mt-2 text-sm leading-6 text-slate-600">{reason.description}</p>
                </div>
              </article>
            );
          })}
        </div>
      </SectionWrapper>

      <SectionWrapper
        eyebrow="Social Proof"
        title="Trusted by teams building real products"
        description="A few words from teams that used GrapplTech to expand capacity, reduce hiring effort, and keep delivery moving."
        className="bg-white"
      >
        <div className="htb-testimonials-grid grid gap-6 lg:grid-cols-3">
          {testimonials.map((testimonial) => (
            <article key={testimonial.name} className="htb-card htb-testimonial-card flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift">
              <div className="htb-logo-box mb-6 flex h-14 w-14 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 p-3">
                <img src={testimonial.logo} alt={testimonial.company} className="max-h-9 max-w-9 object-contain" />
              </div>
              <p className="htb-testimonial-copy flex-1 text-base leading-7 text-slate-600">
                "{testimonial.feedback}"
              </p>
              <div className="htb-testimonial-person mt-6 border-t border-slate-200 pt-5">
                <p className="htb-testimonial-name font-display text-xl font-medium text-slate-900">{testimonial.name}</p>
                <p className="htb-testimonial-role mt-1 text-sm text-slate-500">
                  {testimonial.role}, {testimonial.company}
                </p>
              </div>
            </article>
          ))}
        </div>
      </SectionWrapper>

      <section className="htb-final-section px-5 py-16 sm:px-6 lg:px-8">
        <div className="htb-final-card mx-auto max-w-7xl rounded-[28px] border border-brand-200 bg-gradient-to-br from-brand-700 via-brand-600 to-cyan-500 px-6 py-12 text-center shadow-brand sm:px-10 sm:py-16">
          <FiZap className="mx-auto h-10 w-10 text-white/90" aria-hidden="true" />
          <h2 className="htb-final-title mx-auto mt-5 max-w-3xl font-display text-4xl font-semibold leading-tight tracking-tight text-white">
            Ready to build a dependable product team without slowing down?
          </h2>
          <p className="htb-final-description mx-auto mt-4 max-w-2xl text-base leading-7 text-cyan-50 sm:text-lg">
            Tell us what you are building. We will help you shape the right team,
            shortlist vetted talent, and start with a delivery plan your team can trust.
          </p>
          <a
            href="/contact"
            className="htb-button htb-button-light mt-8 inline-flex h-12 items-center justify-center rounded-xl bg-white px-6 text-sm font-semibold text-brand-700 shadow-soft transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lift active:scale-[0.98]"
          >
            Request a Demo
            <FiArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
          </a>
        </div>
      </section>
    </main>
  );
};

export default HireToBuild;
